/**
 * LoadingTimer - Utility for tracking loading step timings.
 * Collects all step timings and prints a comprehensive summary at the end.
 */

type TimingStep = {
  name: string;
  duration: number;
  timestamp: number;
};

class LoadingTimerClass {
  private startTime = 0;
  private lastStepTime = 0;
  private steps: TimingStep[] = [];
  private isActive = false;

  /**
   * Start a new timing session.
   */
  start(): void {
    this.startTime = performance.now();
    this.lastStepTime = this.startTime;
    this.steps = [];
    this.isActive = true;
  }

  /**
   * Record a timing step. Does nothing if timer is not active.
   * @param name Name of the step
   */
  step(name: string): void {
    if (!this.isActive) return;

    const now = performance.now();
    const duration = Math.round(now - this.lastStepTime);
    const timestamp = Math.round(now - this.startTime);

    this.steps.push({ name, duration, timestamp });
    this.lastStepTime = now;
  }

  /**
   * End the timing session and print a comprehensive summary.
   */
  end(): void {
    if (!this.isActive) return;

    const totalTime = Math.round(performance.now() - this.startTime);
    this.isActive = false;

    // Build summary
    const lines: string[] = [
      "",
      "╔══════════════════════════════════════════════════════════════╗",
      "║                    LOADING TIME SUMMARY                      ║",
      "╠══════════════════════════════════════════════════════════════╣",
    ];

    // Find the longest step name for alignment
    const maxNameLength = Math.max(...this.steps.map((s) => s.name.length), 20);

    for (const step of this.steps) {
      const paddedName = step.name.padEnd(maxNameLength);
      const durationStr = `${step.duration}ms`.padStart(8);
      const timestampStr = `@${step.timestamp}ms`.padStart(10);
      lines.push(`║  ${paddedName} ${durationStr} ${timestampStr}  ║`);
    }

    lines.push(
      "╠══════════════════════════════════════════════════════════════╣"
    );
    const totalLabel = "TOTAL".padEnd(maxNameLength);
    const totalStr = `${totalTime}ms`.padStart(8);
    lines.push(`║  ${totalLabel} ${totalStr}            ║`);
    lines.push(
      "╚══════════════════════════════════════════════════════════════╝"
    );
    lines.push("");

    console.log(lines.join("\n"));
  }

  /**
   * Get elapsed time since start.
   */
  getElapsed(): number {
    return Math.round(performance.now() - this.startTime);
  }

  /**
   * Check if timer is currently active.
   */
  get active(): boolean {
    return this.isActive;
  }
}

// Export singleton instance
export const LoadingTimer = new LoadingTimerClass();
